import React, { useState, useRef, useEffect } from 'react';

export interface FilterOption {
  value: string;
  label: string;
}

interface ColumnFilterProps {
  label: string;
  options: FilterOption[];
  value: string;           // current filter value ('' = no filter)
  onChange: (value: string) => void;
  sortable?: boolean;
  onSort?: () => void;
  sortIcon?: React.ReactNode;
}

/**
 * Excel-style column header filter.
 * - Renders the column label with a small ▾ icon
 * - Click opens a dropdown popover
 * - >5 options: shows a search box + scrollable list
 * - ≤5 options: simple radio-style list
 * - Active filter shows a blue dot indicator
 */
const ColumnFilter: React.FC<ColumnFilterProps> = ({
  label, options, value, onChange, sortable, onSort, sortIcon,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const isSearchable = options.length > 5;
  const isActive = value !== '';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = isSearchable && search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1">
        {sortable ? (
          <button
            className="font-semibold text-left flex items-center gap-0.5 hover:text-blue-700"
            onClick={onSort}
          >
            {label} {sortIcon}
          </button>
        ) : (
          <span className="font-semibold">{label}</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className={`shrink-0 w-4 h-4 flex items-center justify-center rounded text-[10px] transition-colors ${
            isActive
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          title={isActive ? `Filtered: ${options.find(o => o.value === value)?.label || value}` : 'Filter'}
        >
          ▾
        </button>
      </div>

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[180px] max-w-[240px] overflow-hidden"
          style={{ maxHeight: 320 }}>
          {/* Search box for large lists */}
          {isSearchable && (
            <div className="p-2 border-b border-gray-100">
              <input
                className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                placeholder={`Search ${label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}

          {/* Options list */}
          <div className="overflow-y-auto" style={{ maxHeight: isSearchable ? 240 : 260 }}>
            {/* "All" option */}
            <button
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                value === '' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600'
              }`}
              onClick={() => handleSelect('')}
            >
              <span className={`w-3 h-3 rounded-full border flex items-center justify-center ${
                value === '' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
              }`}>
                {value === '' && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
              All
            </button>
            {filtered.map((opt) => (
              <button
                key={opt.value}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                  value === opt.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                }`}
                onClick={() => handleSelect(opt.value)}
              >
                <span className={`w-3 h-3 rounded-full border flex items-center justify-center shrink-0 ${
                  value === opt.value ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}>
                  {value === opt.value && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">No matches</div>
            )}
          </div>

          {/* Clear button when active */}
          {isActive && (
            <div className="border-t border-gray-100 p-1.5">
              <button
                className="w-full text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded py-1 font-medium transition-colors"
                onClick={() => handleSelect('')}
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnFilter;
